// @flow
/* eslint-disable react/style-prop-object */
import * as React from 'react'
import groupBy from 'lodash/groupBy'
import { Helmet } from 'react-helmet'
import DismissIcon from 'icon/DismissIcon'
import DownChevronIcon from 'icon/DownChevronIcon'
import HamburgerMenuIcon from 'icon/HamburgerMenuIcon'
import type { User } from 'types'
import { Button } from 'ui/Form'
import Link from 'ui/Link'
import { applySmallImagePreview } from 'util/uploadcare'
import { Layout, LayoutItem, type LinkManifest } from '../../index'
import { WhitelabelContext } from 'main/framework'
import { AssetName } from 'main/util/asset'

import './PageHeader.module.scss'

type Props = {
  user: User | null,
  linkManifests: LinkManifest[],
  showNavMenuPrompt: boolean,
  showNavMenu: boolean,
  onToggleNavMenu: () => void,
  children: React.Node,
}

export default class BasePageHeaderMobile extends React.Component<Props> {
  static contextType = WhitelabelContext

  render = () => {
    const modifier = this.isNavMenuOpen ? 'showNavMenu' : ''
    const bgColor = this.context.getAsset(AssetName.LOGO_AND_SEARCH_BACKGROUND_COLOR)

    return (
      <div styleName={`root ${modifier}`}>
        <div styleName="bar">
          <Layout>
            <LayoutItem grow>
              <div styleName="children" css={{ backgroundColor: bgColor || 'inherit' }}>
                {this.props.children}{' '}
              </div>
            </LayoutItem>
            <LayoutItem>{this.renderPromptMaybe(bgColor)}</LayoutItem>
          </Layout>
        </div>

        {this.renderNavMenuMaybe()}
      </div>
    )
  }

  renderPromptMaybe = (bgColor?: string) => {
    if (!this.props.showNavMenuPrompt) return

    return this.props.showNavMenu
      ? this.renderDismissNavMenuPrompt()
      : this.renderOpenNavPrompt(bgColor)
  }

  renderDismissNavMenuPrompt = () => {
    return (
      <Prompt isIcon onClick={this.props.onToggleNavMenu}>
        <DismissIcon strokeColor="#28325b" layout="heightConstrained" />
      </Prompt>
    )
  }

  renderOpenNavPrompt = (bgColor?: string) => {
    return this.props.user
      ? this.renderLoggedInNavMenuPrompt(this.props.user, bgColor)
      : this.renderLoggedOutNavMenuPrompt(bgColor)
  }

  renderLoggedInNavMenuPrompt = (user: User, bgColor?: string) => {
    const avatarUrl = applySmallImagePreview(user.avatarUrl)
    return (
      <Prompt onClick={this.props.onToggleNavMenu} bgColor={bgColor}>
        <div styleName="loggedInNavMenuPrompt">
          <img styleName="loggedInNavMenuPromptAvatar" src={avatarUrl} />
          <div styleName="loggedInNavMenuPromptChevron">
            <DownChevronIcon />
          </div>
        </div>
      </Prompt>
    )
  }

  renderLoggedOutNavMenuPrompt = (bgColor?: string) => {
    return (
      <Prompt isIcon onClick={this.props.onToggleNavMenu} bgColor={bgColor}>
        <HamburgerMenuIcon strokeColor="#28325b" layout="heightConstrained" />
      </Prompt>
    )
  }

  renderNavMenuMaybe = () => {
    if (!this.isNavMenuOpen) return

    const { linkManifests } = this.props
    const sectionlessLinkManifests = linkManifests.filter(m => !m.section)
    const groupedLinkManifests = groupBy(linkManifests, 'section')

    return (
      <div styleName="navMenu">
        <Helmet>
          <body style="overflow-y: hidden" />
        </Helmet>
        {this.renderNavSectionMaybe(null, sectionlessLinkManifests)}
        {this.renderNavSectionMaybe('My Account', groupedLinkManifests['My Account'])}
        {this.renderNavSectionMaybe('Wonderschool', groupedLinkManifests.Wonderschool)}
      </div>
    )
  }

  renderNavSectionMaybe = (title: string | null, manifests?: LinkManifest[] = []) => {
    if (manifests.length === 0) return

    return (
      <div styleName="navMenuSection">
        {title && <div styleName="navMenuSectionTitle">{title}</div>}
        {manifests.map((manifest, i) => (
          <NavMenuLink key={i} {...manifest} />
        ))}
      </div>
    )
  }

  get isNavMenuOpen(): boolean {
    return this.props.showNavMenu && this.props.showNavMenuPrompt
  }
}

type PromptProps = {
  isIcon?: boolean,
  onClick: () => void,
  children: React.Node,
  bgColor?: string,
}

function Prompt(props: PromptProps) {
  const modifier = props.isIcon ? 'isIcon' : ''
  return (
    <div styleName="prompt" css={{ backgroundColor: props.bgColor ? props.bgColor : 'inherit' }}>
      <Button appearance="naked" onClick={props.onClick}>
        <div styleName={`promptContent ${modifier}`}>{props.children}</div>
      </Button>
    </div>
  )
}

function NavMenuLink(props: LinkManifest) {
  return (
    <div styleName="navMenuLink">
      <Link blend href={props.href} native={props.native} onClick={props.onClick}>
        {props.text}
      </Link>
    </div>
  )
}
